import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Award, Clock, Users, CheckCircle2, Star } from 'lucide-react';
import { IMAGES } from '@/assets/images';
import { teamMembers, companyInfo } from '@/data/index';
import { TeamCard } from '@/components/Cards';
import { cn } from '@/lib/index';

const stats = [
  {
    label: 'Jahre Erfahrung',
    value: '6+',
    icon: Clock,
  },
  {
    label: 'Servicegebiete',
    value: '2',
    icon: Users,
  },
  {
    label: 'Mobiler Service',
    value: '100%',
    icon: Award,
  },
  {
    label: 'Kundenzufriedenheit',
    value: '99%',
    icon: Shield,
  },
];

const values = [
  {
    title: 'Präzision',
    description: 'Wir arbeiten mit mikroskopischer Genauigkeit, um jede Delle rückstandslos zu entfernen.',
  },
  {
    title: 'Ehrlichkeit',
    description: 'Wir sagen Ihnen offen, was möglich ist und was nicht. Transparente Preise sind für uns selbstverständlich.',
  },
  {
    title: 'Nachhaltigkeit',
    description: 'PDR ist die umweltfreundlichste Reparaturmethode – kein Lack, keine Spachtelmasse, kein Müll.',
  },
  {
    title: 'Leidenschaft',
    description: 'Wir lieben Autos und behandeln jedes Fahrzeug so, als wäre es unser eigenes.',
  },
];

const About: React.FC = () => {
  return (
    <div className="flex flex-col w-full">
      {/* Hero Section */}
      <section className="relative py-24 md:py-32 overflow-hidden bg-foreground text-white">
        <div className="absolute inset-0 z-0 opacity-30">
          <img
            src={IMAGES.MECHANIC_WORK_1}
            alt="Werkstatt Hintergrund"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-foreground/80 via-foreground/50 to-foreground/90" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="inline-block py-1 px-3 rounded-full bg-primary/20 text-primary border border-primary/30 text-sm font-medium mb-6">
              Unsere Geschichte
            </span>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 tracking-tight">
              Über {companyInfo.name}
            </h1>
            <p className="text-xl text-white/80 leading-relaxed">
              Seit über einem Jahrzehnt stehen wir für höchste Qualität in der sanften Delleninstandsetzung. 
              Was als kleine Spezialwerkstatt begann, ist heute die erste Adresse für anspruchsvolle 
              Fahrzeugbesitzer in München.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story & Philosophy Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl font-bold mb-6">Handwerkskunst trifft Technologie</h2>
              <div className="space-y-4 text-muted-foreground leading-relaxed">
                <p>
                  Gegründet im Jahr {companyInfo.establishedYear}, hat sich {companyInfo.name} auf die 
                  Kunst des Ausbeulens ohne Lackieren (PDR) spezialisiert. Unsere Philosophie ist einfach: 
                  Den Originalzustand Ihres Fahrzeugs zu bewahren, ohne chemische Zusätze oder Neulackierung.
                </p>
                <p>
                  Wir verstehen uns nicht nur als Mechaniker, sondern als Chirurgen der Karosserie. 
                  Mit speziellen Werkzeugen und einem geschulten Auge für Lichtreflexionen massieren wir 
                  Dellen millimetergenau aus dem Blech.
                </p>
                <p>
                  Diese Leidenschaft für Perfektion hat uns zum bevorzugten Partner für Privatkunden, 
                  Autohäuser und Versicherungen gemacht.
                </p>
              </div>

              <div className="mt-10 grid grid-cols-2 gap-6">
                {values.map((value, idx) => (
                  <div key={idx} className="flex gap-3">
                    <CheckCircle2 className="w-5 h-5 text-primary shrink-0" />
                    <div>
                      <h4 className="font-semibold text-foreground">{value.title}</h4>
                      <p className="text-sm text-muted-foreground">{value.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-border/50">
                <img
                  src={IMAGES.TOOLS_WORKSHOP_5}
                  alt="PDR Werkzeuge und Präzision"
                  className="w-full aspect-square object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-primary/20 to-transparent" />
              </div>
              
              {/* Floating Badge */}
              <div className="absolute -bottom-6 -right-6 bg-card p-6 rounded-xl shadow-xl border border-border flex items-center gap-4">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                  <Star className="text-primary fill-primary w-6 h-6" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Zertifizierte</p>
                  <p className="font-bold text-foreground">Meisterqualität</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-muted/50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 mb-4">
                  <stat.icon className="w-6 h-6 text-primary" />
                </div>
                <div className="text-3xl font-bold text-foreground mb-1 font-mono">{stat.value}</div>
                <div className="text-sm text-muted-foreground font-medium uppercase tracking-wider">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Lernen Sie unsere Experten kennen</h2>
            <p className="text-muted-foreground">
              Hinter jedem perfekten Ergebnis steht ein Team von Spezialisten, die ihr Handwerk 
              verstehen und lieben.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {teamMembers.map((member) => (
              <TeamCard key={member.id} member={member} />
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="bg-primary rounded-3xl p-12 text-center text-primary-foreground relative overflow-hidden">
            {/* Background Texture Effect */}
            <div className="absolute inset-0 opacity-10 pointer-events-none">
              <div className="absolute top-0 left-0 w-64 h-64 bg-white rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
              <div className="absolute bottom-0 right-0 w-96 h-96 bg-black rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />
            </div>

            <div className="relative z-10 max-w-2xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">Bereit für eine makellose Optik?</h2>
              <p className="text-lg text-primary-foreground/90 mb-8">
                Lassen Sie Ihre Dellen von Profis entfernen. Schnell, kostengünstig und werterhaltend. 
                Kontaktieren Sie uns für eine unverbindliche Ersteinschätzung.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <a
                  href={companyInfo.appointmentUrl}
                  className="px-8 py-4 bg-white text-primary font-bold rounded-xl hover:bg-opacity-90 transition-all shadow-lg"
                >
                  Termin vereinbaren
                </a>
                <a
                  href={`mailto:${companyInfo.email}`}
                  className="px-8 py-4 bg-primary-foreground/10 border border-white/20 text-white font-bold rounded-xl hover:bg-white/10 transition-all"
                >
                  E-Mail senden
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;